import Image from "next/image";
import Link from "next/link";

import { useMemo } from "react";

import { twMerge } from "tailwind-merge";

import CollectionShareButton from "components/CollectionShareButton";
import CustomCard from "components/CustomCard";
import FollowCollectionButton from "components/FollowCollectionButton";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";
import highlightText from "utils/highLightText";

type CollectionSnippetProps = {
  collection: any;
  type?: "default" | "compact" | "card" | "cardV2" | "cardV3";
  compact?: boolean;
  className?: string;
  expandAnimation?: boolean;
  query?: string;
};

const CollectionSnippet = ({
  collection,
  type,
  compact,
  className,
  query,
}: CollectionSnippetProps) => {
  const {
    slug,
    coverImage,
    coverImageUrl: { small, large },
    name,
    description,
  } = collection;

  const { mobile } = useMediaQuery();

  const link = `${emberAppBaseUrl}/collections/${collection.slug}`;
  const title = `${name} ${link}`;

  const shareBtnOptions = useMemo(
    () => ({
      email: {
        link: `mailto:?subject=\u272a Smartkarma | ${name}&body=${title}`,
      },
      clipboard: {
        message: title,
      },
      privateMessage: {
        message: `Collection | ${name}: <a href="${link}">${link}</a>`,
      },
      whatsapp: {
        link: `https://web.whatsapp.com/send?text=${title}`,
      },
      twitter: {
        link: `https://twitter.com/share?url=${link}&text=${title}&via=smartkarma`,
      },
      linkedIn: {
        link: `https://www.linkedin.com/shareArticle?mini=true&url=${link}&source=Smartkarma`,
      },
    }),
    [link, name, title]
  );

  if (type === "cardV2") {
    return (
      <Link
        href={`/collections/${slug}/research`}
        className="flex justify-center flex-1 md:flex-initial"
      >
        <CustomCard
          className={twMerge(
            "w-[144px] h-[112px]",
            "md:w-[184px] md:h-[144px]",
            "p-0",
            "shadow-lg",
            "group",
            className
          )}
        >
          <div
            className={twMerge(
              "relative",
              "rounded-lg",
              "w-[144px] h-[112px]",
              "md:w-[184px] md:h-[144px]"
            )}
          >
            <div
              style={{
                width: mobile ? 144 : 184,
                height: mobile ? 112 : 144,
                backgroundImage: `url(${large})`,
              }}
              className="bg-cover bg-no-repeat bg-center rounded-lg"
            />
            <div
              className={twMerge(
                "transition ease-in-out duration-500 opacity-0 group-hover:opacity-100",
                "hidden md:flex",
                "items-center justify-center space-x-[25px]",
                "absolute inset-0",
                "p-[10px] pb-[32px] md:pb-[42px] ",
                "text-white",
                "bg-[#1F1F20]/80 rounded-lg"
              )}
            >
              <FollowCollectionButton collection={collection} style="icon" />
              <CollectionShareButton collection={collection} style="icon" />
            </div>
            <div
              className={twMerge(
                "absolute bottom-0 left-0 right-0",
                "min-h-[52px] md:min-h-[62px]",
                "p-[10px]",
                "bg-gradient-to-t from-[#1F1F20]/90 to-transparent",
                "rounded-b-lg",
                "flex items-end"
              )}
            >
              <div
                className={twMerge(
                  "text-[12px] md:text-[15px] text-white font-medium text-ellipsis"
                )}
              >
                {name}
              </div>
            </div>
          </div>
        </CustomCard>
      </Link>
    );
  }

  if (type === "cardV3") {
    return (
      <Link
        href={`/collections/${slug}/research`}
        className="basis-full grow lg:grow-0 lg:basis-[calc(50%-15px)]"
      >
        <CustomCard
          className={twMerge(
            "flex items-center justify-between hover:text-black hover:bg-light-gray/40",
            "mb-0 p-[15px] rounded-[15px] w-[100%] shadow-[0_2px_15px_rgba(0,0,0,0.15)]"
          )}
        >
          <div className="flex items-center">
            <Image
              src={small}
              alt={name}
              width={55}
              height={55}
              className="rounded-[5px] mr-[10px] shadow-[0_5px_15px_rgba(0,0,0,0.35)] hover:opacity-90"
            />
            <div className="flex flex-col">
              <span className="text-[16px] font-bold">{name}</span>
              <span className="text-[13px] text-[#444] line-clamp-1">
                {description}
              </span>
            </div>
          </div>
          <FollowCollectionButton
            className="px-[1.25em] py-[0.75em] md:!px-[10px] md:py-[7px]"
            collection={collection}
            hideTextOnActive
            showIcon
          />
        </CustomCard>
      </Link>
    );
  }

  if (type === "card") {
    return (
      <Link
        href={`/collections/${slug}/research`}
        className="flex justify-center"
      >
        <CustomCard
          className={twMerge(
            "w-[144px] h-[112px] p-[10px]",
            "md:w-[288px] md:h-[216px] md:p-[16px]",
            "flex flex-col",
            "shadow-none",
            "border border-solid border-inactive-gray",
            className
          )}
        >
          <div
            className={twMerge(
              "flex-1 max-h-[32px] md:max-h-max md:min-h-[65px]",
              "text-[12px] md:text-[22px] font-black text-ellipsis"
            )}
          >
            {name}
          </div>
          <div
            className={twMerge(
              "relative",
              "w-[124px] h-[55px]",
              "md:w-[256px] md:h-[112px]",
              "mt-[5px]",
              "group"
            )}
          >
            <div
              style={{
                width: mobile ? 124 : 256,
                height: mobile ? 55 : 112,
                backgroundImage: `url(${large})`,
              }}
              className="bg-cover bg-no-repeat bg-center"
            />
            <div
              className={twMerge(
                "transition ease-in-out duration-500 opacity-0 group-hover:opacity-100",
                "hidden md:flex",
                "items-center justify-center space-x-[25px]",
                "absolute top-0 bottom-0 right-0 left-0 p-[10px]",
                "text-white",
                "bg-[#1F1F20]/80"
              )}
            >
              <FollowCollectionButton collection={collection} style="icon" />
              <CollectionShareButton collection={collection} style="icon" />
            </div>
          </div>
        </CustomCard>
      </Link>
    );
  }

  if (compact || type === "compact") {
    return (
      <div
        className={twMerge(
          "px-[15px] py-[10px]",
          "flex items-center",
          "space-x-[7px]"
        )}
      >
        <Link href={`/collections/${slug}/analyis`}>
          <div className={twMerge()}>
            <Image
              src={small}
              alt={name}
              width={35}
              height={35}
              className="rounded-[5px]"
            />
          </div>
        </Link>
        <Link
          href={`/collections/${slug}`}
          className={twMerge("text-active-black", "flex-1 flex items-center")}
        >
          <div className="text-[14px] font-medium">
            {" "}
            {query ? highlightText(name, query) : name}
          </div>
        </Link>
        <FollowCollectionButton collection={collection} style="small" />
      </div>
    );
  }

  return (
    <div className={twMerge("px-[15px]", "py-[10px]", "overflow-hidden")}>
      <Link
        className={twMerge(
          "relative",
          "block",
          "group",
          "md:w-[270px]",
          "h-[98px]",
          "overflow-hidden",
          "rounded-[15px]"
        )}
        href={`/collections/${slug}/research`}
      >
        <div
          className={twMerge(
            "absolute",
            "bottom-0",
            "top-0",
            "right-0",
            "left-0",
            "md:w-[270px]",
            "h-[98px]",
            "scale-100",
            "group-hover:scale-125",
            "ease-in",
            "duration-200"
          )}
        >
          <Image
            src={coverImage}
            alt={name}
            width={300}
            height={98}
            layout={"responsive"}
            className="rounded-[15px]"
          />
        </div>
        <div
          className={twMerge(
            "absolute bottom-0 top-0 right-0 left-0 p-[10px] flex flex-col justify-end text-white",
            "bg-gradient-to-t from-black to-transparent"
          )}
        >
          <div className="mb-[10px] text-[12px] text-ellipsis">
            {query ? highlightText(description, query) : description}
          </div>
          <div className="mb-[5px] text-[18px] font-medium leading-0">
            {query ? highlightText(name, query) : name}
          </div>
        </div>
      </Link>
    </div>
  );
};

export default CollectionSnippet;
